import { Application, Assets, Sprite, Texture } from 'pixi.js';
import type { ClimberPose } from '@shared/types';
import { loadStarterSkin } from '@/characters/starterSkin';
import { resolveAssetUrl } from '@/shop/assetUrl';

// ─── Climber sprite (PNG) ───────────────────────────────────────────

// Last-resort built-in climber art. Only used if the starter skin can't be
// fetched (e.g. first-ever offline launch). Has a baked opaque bg, so it's the
// fallback of last resort — the starter skin (transparent) is preferred below.
const FALLBACK_TEXTURE_PATHS: Record<ClimberPose, string> = {
  idle: '/assets/characters/red_idle.png',
  climb: '/assets/characters/red_climb.png',
  fall: '/assets/characters/red_fall.png',
  victory: '/assets/characters/red_idle.png',
};

interface ClimberTextureSet {
  idle: Texture;
  climb: Texture;
  fall: Texture;
  victory: Texture;
}

let textureCache: ClimberTextureSet | null = null;
let loadingPromise: Promise<ClimberTextureSet> | null = null;

/** Resolve the default climber texture paths: the admin-set starter skin (the
 * universal default look — transparent art) with the built-in red_*.png as a
 * last-resort fallback so a match still renders if the skin can't be fetched. */
async function resolveDefaultPaths(): Promise<Record<ClimberPose, string>> {
  try {
    const skin = await loadStarterSkin();
    const idle = resolveAssetUrl(skin?.idleUrl);
    const climb = resolveAssetUrl(skin?.climbUrl);
    const fall = resolveAssetUrl(skin?.fallUrl);
    if (idle && climb && fall) {
      return { idle, climb, fall, victory: resolveAssetUrl(skin?.winUrl) ?? idle };
    }
  } catch {
    /* fall through to the built-in art */
  }
  return FALLBACK_TEXTURE_PATHS;
}

export function loadClimberTextures(): Promise<ClimberTextureSet> {
  if (textureCache) return Promise.resolve(textureCache);
  if (loadingPromise) return loadingPromise;
  loadingPromise = (async () => {
    const load = async (paths: Record<ClimberPose, string>): Promise<ClimberTextureSet> => {
      const [idle, climb, fall] = await Promise.all([
        Assets.load<Texture>(paths.idle),
        Assets.load<Texture>(paths.climb),
        Assets.load<Texture>(paths.fall),
      ]);
      const victory =
        paths.victory === paths.idle
          ? idle
          : await Assets.load<Texture>(paths.victory).catch(() => idle);
      return { idle, climb, fall, victory };
    };
    try {
      textureCache = await load(await resolveDefaultPaths());
    } catch {
      // Starter art failed mid-load — fall back to the built-in climber so the
      // match still renders something instead of an empty lane.
      textureCache = await load(FALLBACK_TEXTURE_PATHS);
    }
    return textureCache;
  })();
  return loadingPromise;
}

// Drop the local cache so the next MatchScene re-resolves textures from Pixi's
// Assets registry. We deliberately do NOT call Assets.unload here: in dev with
// React's double-mount, two scenes may be initializing in parallel and
// unloading mid-load corrupts the global Assets cache for the live scene.
export function resetClimberTextureCache(): void {
  textureCache = null;
  loadingPromise = null;
}

export function getClimberTexture(pose: ClimberPose): Texture | null {
  if (!textureCache) return null;
  return textureCache[pose];
}

// Used by the SVG fallback path — no-op if textures load successfully.
export async function preloadAssets(_app: Application): Promise<void> {
  await loadClimberTextures().catch(() => {
    /* keep going; the scene will draw a placeholder until the texture lands */
  });
}

export function makeFallbackClimber(): Sprite {
  // 1×1 white pixel — gets tinted + scaled when no PNG is available yet.
  const tex = Texture.WHITE;
  const s = new Sprite(tex);
  s.tint = 0xe63946;
  return s;
}
